import React, { useState, useEffect } from "react";
import { X, Loader } from "lucide-react";
import { useApi } from "../hooks/useApi";

const SurfaceScanEnrollDealModal = ({ isOpen, onClose, onEnrolled, existingDealIds = [], existingDomains = [] }) => {
    const [deals, setDeals] = useState([]);
    const [selectedDealId, setSelectedDealId] = useState("");
    const [domain, setDomain] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const apiCall = useApi();

    useEffect(() => {
        if (isOpen) {
            setSelectedDealId("");
            setDomain("");
            setError(null);
            fetchDeals();
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchDeals = async () => {
        setLoading(true);
        try {
            const res = await apiCall("GET", `/deals/all`);
            const available = (res || []).filter(d => !existingDealIds.includes(d.dealID));
            setDeals(available);
        } catch (err) {
            setError("Failed to load deals");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedDealId) return;

        const d = domain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
        if (!d) {
            setError("A domain is required for surface scan monitoring.");
            return;
        }
        if (existingDomains.some(x => x.toLowerCase() === d)) {
            setError("This domain is already enrolled for surface scan monitoring.");
            return;
        }

        setSaving(true);
        setError(null);
        try {
            onEnrolled?.(parseInt(selectedDealId, 10), d);
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to enroll deal");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-6 border border-border w-full max-w-lg shadow-lg rounded-md bg-theme-secondary">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">Add Deal for Surface Scan Monitoring</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-2">Deal</label>
                        <select
                            value={selectedDealId}
                            onChange={(e) => {
                                const id = e.target.value;
                                setSelectedDealId(id);
                                const deal = deals.find(d => d.dealID.toString() === id);
                                setDomain(deal?.domain || "");
                            }}
                            className="w-full border border-border rounded-md px-3 py-2 bg-theme-primary text-content-primary"
                            disabled={loading}
                        >
                            <option value="">-- Select a Deal --</option>
                            {deals.map((d) => (
                                <option key={d.dealID} value={d.dealID} title={d.dealName}>
                                    {d.dealName}
                                </option>
                            ))}
                        </select>
                        {!loading && deals.length === 0 && (
                            <p className="text-sm text-content-secondary mt-2">
                                No deals available for enrollment.
                            </p>
                        )}
                    </div>

                    {selectedDealId && (
                        <div>
                            <label className="block text-sm font-medium text-content-secondary mb-1">Domain</label>
                            <input
                                type="text"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                placeholder="e.g., example.com"
                                className="w-full border border-border rounded-md px-3 py-2 bg-theme-primary text-content-primary"
                            />
                            <p className="mt-1 text-sm text-content-secondary">
                                Enter the domain to monitor for this deal.
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-border text-content-secondary rounded-md hover:bg-theme-primary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!selectedDealId || saving}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                "Add Deal"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SurfaceScanEnrollDealModal;
